import React, { useEffect, useState } from "react";
import Map from "./Map";
import LeftBar from "@components/Layout/LeftBar";
import styled from "@emotion/styled";
import FieldsMenu from "./FieldsMenu";
import {
  FieldsScreenVMProvider,
  useFieldsScreenVM,
} from "@screens/FieldsScreen/FieldsScreenVM";
import { useStores } from "@stores";
import { observer } from "mobx-react-lite";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import MobileTabBar, { TAB_BAR_HEIGHT } from "@components/MobileTabBar";
import MobileDrawer from "@components/MobileDrawer";
import { ReactComponent as MapIcon } from "@assets/icons/map.svg";
import { useLang } from "@src/hooks/useLang";

interface IProps {}

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  min-width: 0;
  align-items: stretch;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: var(--vh-full, 100vh);
  }
`;

const MobileMenuButton = styled.button`
  position: fixed;
  bottom: calc(${TAB_BAR_HEIGHT + 16}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px)));
  left: 16px;
  height: 40px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 20px;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 13px;
  color: #494c5b;
  cursor: pointer;
  z-index: 100;
  svg {
    width: 16px;
    height: 16px;
  }
  path {
    fill: #68ad18;
  }
`;

const MapResizeObserver: React.FC = () => {
  const vm = useFieldsScreenVM();
  const { settingsStore } = useStores();

  useEffect(() => {
    const timer = setTimeout(() => vm.map.updateSize(), 300);
    return () => clearTimeout(timer);
  }, [settingsStore.leftBarExpanded, vm.map]);

  return null;
};

const FieldsScreenImpl: React.FC<IProps> = () => {
  const isMobile = useIsMobile();
  const lang = useLang();
  const [menuOpen, setMenuOpen] = useState(false);


  return (
    <FieldsScreenVMProvider>
      <MapResizeObserver />
      <Root>
        <LeftBar style={{ marginRight: 0 }} />
        {!isMobile && <FieldsMenu />}
        <Map />
        {isMobile && (
          <>
            <MobileMenuButton onClick={() => setMenuOpen(true)}>
              <MapIcon />
              {lang.menu.fieldMenuOption}
            </MobileMenuButton>
            <MobileDrawer open={menuOpen} onClose={() => setMenuOpen(false)}>
              <FieldsMenu onSelected={() => setMenuOpen(false)} />
            </MobileDrawer>
            <MobileTabBar />
          </>
        )}
      </Root>
    </FieldsScreenVMProvider>
  );
};

const FieldsScreen: React.FC = () => <FieldsScreenImpl />;

export default observer(FieldsScreen);
